/**
 * ****************************************************************************
 * @description     :   Store data and process common will use in app
 * @created at      :   2024/05/22
 * @created by      :   ANS903 – phuongdt@ans-asia.com
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import messages, { MSG_TYPE, MSG_TITLE } from '@/utils/messages';
import repository from '@/repository';
import helpers from '@/utils/helpers';

// Helper functions for popup state management
const getDefaultPopupDimensions = () => ({
    width: '75%',
    height: '90%',
    maxWidth: null,
    maxHeight: null
});

const getDefaultPopupStyles = () => ({
    overlay: {},
    wrapper: {},
    header: {},
    logo: {},
    title: {},
    body: {},
    footer: {},
    closeButton: {}
});

const getDefaultPopupClasses = () => ({
    overlay: '',
    wrapper: '',
    header: '',
    logo: '',
    title: '',
    body: '',
    footer: '',
    closeButton: ''
});

const getDefaultPopupOptions = () => ({
    showHeader: true,
    showLogo: true,
    showCloseButton: true,
    customActionCloseButton: {
        isReplace: false,
        callbacks: () => {}
    },
    showFooter: false,
    closeOnOverlay: false,
    closeOnEscape: true,
    pureContent: false
});

const getDefaultCustomComponents = () => ({
    listComponents: [],
    class: {},
    style: {}
});

/**
 * Get default persist options for popup
 * @returns {Object} Default persist options
 * - id: Unique identifier for the popup (required for persistence)
 *       If id is provided, popup will be hidden (persisted) instead of destroyed on close
 *       If id is null/undefined, popup will be destroyed on close (default behavior)
 */
const getDefaultPersistOptions = () => ({
    id: null
});

const createPopupFromPayload = (payload, zIndex = null) => {
    const baseStyles =
        zIndex !== null
            ? {
                  overlay: { zIndex, ...(payload.styles?.overlay || {}) },
                  wrapper: { zIndex: zIndex + 1, ...(payload.styles?.wrapper || {}) },
                  header: payload.styles?.header || {},
                  logo: payload.styles?.logo || {},
                  title: payload.styles?.title || {},
                  body: payload.styles?.body || {},
                  footer: payload.styles?.footer || {},
                  closeButton: payload.styles?.closeButton || {}
              }
            : { ...getDefaultPopupStyles(), ...(payload.styles || {}) };

    return {
        component: payload.component,
        componentProps: payload.componentProps || {},
        title: payload.title || '',
        logoSrc: payload.logoSrc || '',
        dimensions: { ...getDefaultPopupDimensions(), ...(payload.dimensions || {}) },
        styles: baseStyles,
        classes: { ...getDefaultPopupClasses(), ...(payload.classes || {}) },
        options: { ...getDefaultPopupOptions(), ...(payload.options || {}) },
        customComponents: { ...getDefaultCustomComponents(), ...(payload.customComponents || {}) },
        persist: { ...getDefaultPersistOptions(), ...(payload.persist || {}) },
        onClose: payload.onClose || null,
        onSelect: payload.onSelect || null,
        eventHandlers: payload.eventHandlers || {}
    };
};

export default {
    namespaced: true,
    state: {
        isLoading: false,
        isForceLoading: false,
        isNoLoading: false,
        countLoading: 0,
        isShowHeaderError: false,
        headerErrors: [],
        isShowModalMessage: false,
        isShowPtjModalMessage: false,
        S9007: [],
        modalMessage: {
            // type: MSG_TYPE.ERROR,
            type: MSG_TYPE.SUCCESS,
            title: '',
            content: '',
            okText: 'はい',
            cancelText: 'いいえ',
            customText: '初期化',
            showBtnCancelWarning: false,
            callback: () => {},
            callbackCancel: () => {},
            callbackCustom: () => {}
        },
        ptjModalMessage: {
            // type: MSG_TYPE.ERROR,
            type: MSG_TYPE.SUCCESS,
            title: '',
            content: '',
            okText: 'はい',
            cancelText: 'いいえ',
            customText: '初期化',
            showBtnCancelWarning: false,
            callback: () => {},
            callbackCancel: () => {},
            callbackCustom: () => {}
        },
        header: {
            title: '',
            showBack: false,
            isDisabledBack: false,
            isShowReload: false,
            fixBc: false,
            topButtons: [],
            topMessage: false,
            progressBar: {
                display: false,
                stepCurrent: 0,
                stepTotal: 0
            },
            onBack: () => {
                helpers.func_back();
            }
        },
        footer: {
            footerButtons: []
        },
        footerModals: [],
        isExistsLeftMenu: false,
        isShowLeftMenu: localStorage.getItem('isPinIconLeftMenu') === 'true' ? true : false,
        isShowTutorial: false,
        screenId: 'login',
        isDataScreenChange: false,
        isLogout: false,
        isLogin: false,
        user: {},
        menus: [],
        screenAuthority: 0,
        isBacking: false,
        notification: [],
        countNotify: 0,
        showSideBar: true,
        isReportLinkMenu: false,
        urlASP: '',
        zoom: 100,
        order_number: 0,
        isFaqLayout: false,
        // Global Wrapper State
        globalWrapper: {
            visible: false,
            component: null,
            componentProps: {},
            triggerElement: null,
            onClose: null,
            onSelect: null
        },
        // Global Popup State - Unified approach
        popups: [], // Array of all visible popups (single or multiple layers)
        popupStack: [], // Stack to store hidden popups (for single mode restoration)
        popupIdCounter: 0, // Counter for generating unique popup IDs
        baseZIndex: 9999, // Base z-index for popups
        hiddenPopups: {}, // Storage for persistent hidden popups (keyed by persist.id)
        // Global Widget State
        widget: {
            isVisible: false,
            showContent: false,
            component: null,
            componentProps: {},
            position: 'bottom-right',
            isMobile: false,
            iconProps: {},
            showCloseButton: true,
            showIconToggle: false,
            styles: {
                container: {},
                icon: {},
                content: {},
                closeButton: {}
            },
            classes: {
                container: '',
                icon: '',
                content: '',
                closeButton: ''
            },
            offset: {
                x: 20,
                y: 20
            },
            onClose: null,
            onIconClick: null
        },
        isScroll: false,
        // Navigation State - Route props without URL change
        routeProps: {},
        // MNG_DM_Messsage
        dmMessageMode: 'receive',
        dmCount: 0
    },
    getters: {
        getRouteProps: (state) => (routeName) => {
            return state.routeProps[routeName] || {};
        },

        hasRouteProps: (state) => (routeName) => {
            return !!state.routeProps[routeName] && Object.keys(state.routeProps[routeName]).length > 0;
        }
    },
    mutations: {
        showLoading(state) {
            state.isLoading = true;
        },
        hideLoading(state) {
            state.isLoading = false;
        },
        showForceLoading(state) {
            state.isForceLoading = true;
        },
        hideForceLoading(state) {
            state.isForceLoading = false;
        },
        showHeaderError(state, errors) {
            state.isShowHeaderError = true;
            state.headerErrors = errors;
        },
        hideHeaderError(state) {
            state.isShowHeaderError = false;
            state.headerErrors = [];
        },
        showModalMessage(state, message) {
            state.isShowHeaderError = false;
            state.headerErrors = [];
            state.isShowModalMessage = true;
            if (message) {
                if (message.type) {
                    state.modalMessage.type = message.type;
                } else {
                    // state.modalMessage.type = MSG_TYPE.ERROR;
                    state.modalMessage.type = MSG_TYPE.SUCCESS;
                }
                state.modalMessage.title = message.title;
                if (message.content) {
                    state.modalMessage.content = message.content;
                } else {
                    state.modalMessage.content = messages.E999;
                }
                if (message.okText) {
                    state.modalMessage.okText = message.okText;
                } else {
                    state.modalMessage.okText = 'はい';
                }
                if (message.cancelText) {
                    state.modalMessage.cancelText = message.cancelText;
                } else {
                    state.modalMessage.cancelText = 'いいえ';
                }
                if (message.customText) {
                    state.modalMessage.customText = message.customText;
                } else {
                    state.modalMessage.customText = '初期化';
                }
                state.modalMessage.showBtnCancelWarning = message.showBtnCancelWarning;
                state.modalMessage.callback = message.callback;
                state.modalMessage.callbackCancel = message.callbackCancel;
                state.modalMessage.callbackCustom = message.callbackCustom;
            } else {
                state.modalMessage = {
                    type: MSG_TYPE.ERROR,
                    title: MSG_TITLE.E999,
                    content: messages.E999,
                    okText: 'はい',
                    cancelText: 'いいえ',
                    showBtnCancelWarning: false,
                    callback: () => {},
                    callbackCancel: () => {},
                    callbackCustom: () => {}
                };
            }
        },
        updateShowModalMessage(state, value) {
            state.isShowModalMessage = value;
        },
        hideModalMessage(state) {
            state.isShowModalMessage = false;
        },
        showPtjModalMessage(state, message) {
            state.isShowHeaderError = false;
            state.headerErrors = [];
            state.isShowPtjModalMessage = true;
            if (message) {
                if (message.type) {
                    state.ptjModalMessage.type = message.type;
                } else {
                    // state.modalMessage.type = MSG_TYPE.ERROR;
                    state.ptjModalMessage.type = MSG_TYPE.SUCCESS;
                }
                state.ptjModalMessage.title = message.title;
                if (message.content) {
                    state.ptjModalMessage.content = message.content;
                } else {
                    state.ptjModalMessage.content = messages.E999;
                }
                if (message.okText) {
                    state.ptjModalMessage.okText = message.okText;
                } else {
                    state.ptjModalMessage.okText = 'はい';
                }
                if (message.cancelText) {
                    state.ptjModalMessage.cancelText = message.cancelText;
                } else {
                    state.ptjModalMessage.cancelText = 'いいえ';
                }
                if (message.customText) {
                    state.ptjModalMessage.customText = message.customText;
                } else {
                    state.ptjModalMessage.customText = '初期化';
                }
                state.ptjModalMessage.showBtnCancelWarning = message.showBtnCancelWarning;
                state.ptjModalMessage.callback = message.callback;
                state.ptjModalMessage.callbackCancel = message.callbackCancel;
                state.ptjModalMessage.callbackCustom = message.callbackCustom;
            } else {
                state.ptjModalMessage = {
                    type: MSG_TYPE.ERROR,
                    title: MSG_TITLE.E999,
                    content: messages.E999,
                    okText: 'はい',
                    cancelText: 'いいえ',
                    showBtnCancelWarning: false,
                    callback: () => {},
                    callbackCancel: () => {},
                    callbackCustom: () => {}
                };
            }
        },
        updateShowPtjModalMessage(state, value) {
            state.isShowPtjModalMessage = value;
        },
        hidePtjModalMessage(state) {
            state.isShowPtjModalMessage = false;
        },
        setHeader(state, header) {
            if (header === null || header === undefined || header === '' || (Array.isArray(header) && header.length === 0)) {
                state.header = {
                    title: '',
                    showBack: false,
                    isDisabledBack: false,
                    isShowReload: false,
                    fixBc: false,
                    topButtons: [],
                    progressBar: {
                        display: false,
                        stepCurrent: 0,
                        stepTotal: 0
                    },
                    onBack: () => {
                        helpers.func_back();
                    }
                };
            } else if (typeof header === 'object' && Object.keys(header).length > 0) {
                state.header = {
                    ...state.header,
                    ...header
                };

                // if (header.onBack) {
                //     let newOnBack = (e) => {
                //         e.preventDefault();
                //         if (e.which === 1) {
                //             header.onBack();
                //         }
                //     };

                //     state.header.onBack = newOnBack;
                // }
            }
        },
        setFooterFunction(state, footer) {
            if (footer.footerButtons && footer.footerButtons.length > 0) {
                for (let i = 0; i < footer.footerButtons.length; i++) {
                    if (!footer.footerButtons[i].isDisabled) footer.footerButtons[i].isDisabled = false;
                }
            }
            state.footer.footerButtons = footer.footerButtons;
        },
        setFooterModals(state, footerModals) {
            state.footerModals = footerModals.footerModals;
        },
        setZoom(state, val) {
            state.zoom = val;
        },
        setShowLeftMenu(state, status) {
            state.isShowLeftMenu = status;
        },
        setShowCusTutorial(state, status) {
            state.isShowTutorial = status;
        },
        setExistsLeftMenu(state, status) {
            state.isExistsLeftMenu = status;
        },
        setAppState(state, data) {
            state.menus = data.menus;
            state.user = data.user;
        },
        resetAppState(state) {
            state.menus = [];
            state.user = {};
        },
        setScreenId(state, id) {
            state.screenId = id;
        },
        setNoLoading(state, val) {
            state.isNoLoading = val;
        },
        increaseCountLoading(state) {
            state.countLoading += 1;
        },
        decreaseCountLoading(state) {
            state.countLoading -= 1;
            if (state.countLoading < 0) {
                state.countLoading = 0;
            }
        },
        setOpenSideBarState(state, flag) {
            state.showSideBar = flag;
        },
        setDataScreenChange(state, data) {
            state.isDataScreenChange = data;
        },
        setLogout(state, data) {
            state.isLogout = data;
        },
        setLogin(state, data) {
            state.isLogin = data;
        },
        setScreenAuthority(state, data) {
            state.screenAuthority = data;
        },
        setBacking(state, data) {
            state.isBacking = data;
        },
        setDM(state, data) {
            state.dmCount = data;
        },
        setNotification(state, data) {
            if (data.notify && data.notify.length > 0) {
                state.notification = data?.notify ?? [];
                state.countNotify = data?.notify[0]?.total_cnt ?? 0;
            } else {
                state.countNotify = 0;
            }
            // const lengthNotify = data.notify.length;
            // if (lengthNotify < 10) {
            //     for (let i = 0; i < 10 - lengthNotify; i++) {
            //         state.notification.push({
            //             notification_id: i,
            //             show_content: '',
            //             period_from: null,
            //             period_to: null
            //         });
            //     }
            // }
        },
        setIsReportLinkMenu(state, data) {
            state.isReportLinkMenu = data;
        },
        setUrlASP(state, data) {
            state.urlASP = data;
        },
        setMessageS9007(state, data) {
            state.S9007 = data;
        },
        setUserInfo(state, data) {
            state.user = data;
        },
        resetUserInfo(state) {
            state.user = {};
        },
        setOrderNumber(state, data) {
            state.order_number = data;
        },
        setIsFaqLayout(state, value) {
            state.isFaqLayout = value;
        },
        // Global Wrapper Mutations
        showGlobalWrapper(state, payload) {
            state.globalWrapper.visible = true;
            state.globalWrapper.component = payload.component;
            state.globalWrapper.componentProps = payload.componentProps || {};
            state.globalWrapper.triggerElement = payload.triggerElement || null;
            state.globalWrapper.onClose = payload.onClose || null;
            state.globalWrapper.onSelect = payload.onSelect || null;
        },
        hideGlobalWrapper(state) {
            state.globalWrapper.visible = false;
            state.globalWrapper.component = null;
            state.globalWrapper.componentProps = {};
            state.globalWrapper.triggerElement = null;
            state.globalWrapper.onClose = null;
            state.globalWrapper.onSelect = null;
        },
        // Popup Mutations
        showPopup(state, payload) {
            const isStillShowPopupBefore = payload.isStillShowPopupBefore || false;
            const persistId = payload.persist?.id || null;

            // Check if restoring a hidden popup with persist.id
            if (persistId && state.hiddenPopups[persistId]) {
                const restoredPopup = state.hiddenPopups[persistId];
                delete state.hiddenPopups[persistId];

                if (!isStillShowPopupBefore) {
                    if (state.popups.length > 0) {
                        state.popupStack.push(...state.popups);
                        state.popups = [];
                    }
                }

                // Restore with updated z-index
                const zIndex = state.baseZIndex + state.popups.length * 2;
                state.popups.push({
                    ...restoredPopup,
                    zIndex: zIndex,
                    isVisible: true,
                    shouldRestoreStack: !isStillShowPopupBefore,
                    styles: {
                        ...restoredPopup.styles,
                        overlay: { ...restoredPopup.styles.overlay, zIndex },
                        wrapper: { ...restoredPopup.styles.wrapper, zIndex: zIndex + 1 }
                    }
                });
                return;
            }

            if (!isStillShowPopupBefore) {
                // Single mode: Move current popups to stack (hide them, don't close)
                if (state.popups.length > 0) {
                    state.popupStack.push(...state.popups);
                    state.popups = [];
                }
            }

            // Add new popup to array
            const popupId = ++state.popupIdCounter;
            const zIndex = state.baseZIndex + state.popups.length * 2;

            state.popups.push({
                id: popupId,
                zIndex: zIndex,
                isVisible: true,
                shouldRestoreStack: !isStillShowPopupBefore, // Flag to restore stack on close
                ...createPopupFromPayload(payload, zIndex)
            });
        },
        hidePopup(state) {
            // Close the last (topmost) popup
            if (state.popups.length > 0) {
                const popup = state.popups.pop();
                const shouldRestoreStack = popup.shouldRestoreStack;

                // Check if popup should be persisted (hidden) instead of destroyed
                if (popup.persist?.id) {
                    // Save popup state to hiddenPopups for later restoration
                    state.hiddenPopups[popup.persist.id] = {
                        ...popup,
                        isVisible: false
                        // Note: Don't call onClose when hiding for persistence
                    };
                } else {
                    // Original behavior: Call onClose callback
                    if (popup.onClose && typeof popup.onClose === 'function') {
                        popup.onClose();
                    }
                }

                // Restore popups from stack if needed (single mode)
                if (shouldRestoreStack && state.popupStack.length > 0) {
                    // Restore with recalculated z-index
                    const restoredPopups = state.popupStack.map((p, idx) => ({
                        ...p,
                        zIndex: state.baseZIndex + idx * 2,
                        styles: {
                            ...p.styles,
                            overlay: {
                                ...p.styles.overlay,
                                zIndex: state.baseZIndex + idx * 2
                            },
                            wrapper: {
                                ...p.styles.wrapper,
                                zIndex: state.baseZIndex + idx * 2 + 1
                            }
                        }
                    }));
                    state.popups.push(...restoredPopups);
                    state.popupStack = [];
                }
            }
        },
        closePopupById(state, popupId) {
            const index = state.popups.findIndex((p) => p.id === popupId);
            if (index !== -1) {
                const popup = state.popups[index];
                const shouldRestoreStack = popup.shouldRestoreStack;

                // Check if popup should be persisted (hidden) instead of destroyed
                if (popup.persist?.id) {
                    // Save popup state to hiddenPopups for later restoration
                    state.hiddenPopups[popup.persist.id] = {
                        ...popup,
                        isVisible: false
                        // Note: Don't call onClose when hiding for persistence
                    };
                } else {
                    // Original behavior: Call onClose callback
                    if (popup.onClose && typeof popup.onClose === 'function') {
                        popup.onClose();
                    }
                }

                // Remove popup from array
                state.popups.splice(index, 1);

                // Restore popups from stack if needed (single mode)
                if (shouldRestoreStack && state.popupStack.length > 0) {
                    // Restore with recalculated z-index
                    const restoredPopups = state.popupStack.map((p, idx) => ({
                        ...p,
                        zIndex: state.baseZIndex + idx * 2,
                        styles: {
                            ...p.styles,
                            overlay: {
                                ...p.styles.overlay,
                                zIndex: state.baseZIndex + idx * 2
                            },
                            wrapper: {
                                ...p.styles.wrapper,
                                zIndex: state.baseZIndex + idx * 2 + 1
                            }
                        }
                    }));
                    state.popups.push(...restoredPopups);
                    state.popupStack = [];
                }
            }
        },
        hideAllPopups(state) {
            // Call onClose for all visible popups
            state.popups.forEach((popup) => {
                if (popup.onClose && typeof popup.onClose === 'function') {
                    popup.onClose();
                }
            });

            // Call onClose for all stacked popups
            state.popupStack.forEach((popup) => {
                if (popup.onClose && typeof popup.onClose === 'function') {
                    popup.onClose();
                }
            });

            state.popups = [];
            state.popupStack = [];
        },
        /**
         * Clear a specific hidden popup by persist.id
         * Use this when navigating away from a page or when you want to destroy a persisted popup
         * @param {string} persistId - The persist.id of the popup to clear
         */
        clearHiddenPopup(state, persistId) {
            if (state.hiddenPopups[persistId]) {
                const popup = state.hiddenPopups[persistId];
                // Call onClose callback when explicitly clearing
                if (popup.onClose && typeof popup.onClose === 'function') {
                    popup.onClose();
                }
                delete state.hiddenPopups[persistId];
            }
        },
        /**
         * Clear all hidden popups
         * Use this when you want to reset all persisted popup states
         */
        clearAllHiddenPopups(state) {
            Object.values(state.hiddenPopups).forEach((popup) => {
                if (popup.onClose && typeof popup.onClose === 'function') {
                    popup.onClose();
                }
            });
            state.hiddenPopups = {};
        },
        updatePopupConfig(state, payload) {
            // Update the topmost (last) popup in the array
            if (state.popups.length > 0) {
                const topmostPopup = state.popups[state.popups.length - 1];

                // Deep merge helper function
                const deepMerge = (target, source) => {
                    for (const key in source) {
                        if (Object.prototype.hasOwnProperty.call(source, key)) {
                            if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                                // If target doesn't have this property, create new object
                                target[key] = target[key] || {};
                                // Recursive merge
                                deepMerge(target[key], source[key]);
                            } else {
                                // Primitive value or array: overwrite directly
                                target[key] = source[key];
                            }
                        }
                    }
                    return target;
                };

                // Deep merge payload into popup
                deepMerge(topmostPopup, payload);
            }
        },
        // Widget Mutations
        showWidget(state, payload) {
            state.widget.isVisible = true;
            state.widget.component = payload.component;
            state.widget.componentProps = payload.componentProps || {};
            state.widget.position = payload.position || 'bottom-right';
            state.widget.isMobile = payload.isMobile || false;
            state.widget.iconProps = payload.iconProps || {};
            state.widget.showCloseButton = payload.showCloseButton !== undefined ? payload.showCloseButton : true;
            state.widget.showIconToggle = payload.showIconToggle !== undefined ? payload.showIconToggle : false;
            state.widget.styles = {
                ...state.widget.styles,
                ...(payload.styles || {})
            };
            state.widget.classes = {
                ...state.widget.classes,
                ...(payload.classes || {})
            };
            state.widget.offset = {
                ...state.widget.offset,
                ...(payload.offset || {})
            };
            state.widget.onClose = payload.onClose || null;
            state.widget.onIconClick = payload.onIconClick || null;
            state.widget.draggable = payload.draggable || false;
        },
        hideWidget(state) {
            if (state.widget.onClose && typeof state.widget.onClose === 'function') {
                state.widget.onClose();
            }
            state.widget.isVisible = false;
            state.widget.showContent = false;
            state.widget.component = null;
            state.widget.componentProps = {};
            state.widget.onClose = null;
            state.widget.onIconClick = null;
            // Reset to defaults
            state.widget.position = 'bottom-right';
            state.widget.isMobile = false;
            state.widget.iconProps = {};
            state.widget.showCloseButton = true;
            state.widget.showIconToggle = false;
            state.widget.styles = {
                container: {},
                icon: {},
                content: {},
                closeButton: {}
            };
            state.widget.classes = {
                container: '',
                icon: '',
                content: '',
                closeButton: ''
            };
            state.widget.offset = {
                x: 20,
                y: 20
            };
            state.widget.draggable = false;
        },
        toggleWidgetContent(state, show = null) {
            if (show !== null) {
                state.widget.showContent = show;
            } else {
                state.widget.showContent = !state.widget.showContent;
            }
        },
        updateWidgetConfig(state, payload) {
            Object.keys(payload).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(state.widget, key)) {
                    if (typeof state.widget[key] === 'object' && state.widget[key] !== null) {
                        state.widget[key] = {
                            ...state.widget[key],
                            ...payload[key]
                        };
                    } else {
                        state.widget[key] = payload[key];
                    }
                }
            });
        },
        setIsScroll(state, value) {
            state.isScroll = value;
        },
        setDmMessageMode(state, value) {
            state.dmMessageMode = value;
        },
        // Route Props Mutations
        setRouteProps(state, { routeName, props }) {
            state.routeProps[routeName] = { ...props };
        },
        clearRouteProps(state, routeName) {
            delete state.routeProps[routeName];
        },
        clearAllRouteProps(state) {
            state.routeProps = {};
        },
        updateRouteProp(state, { routeName, key, value }) {
            if (!state.routeProps[routeName]) {
                state.routeProps[routeName] = {};
            }
            state.routeProps[routeName][key] = value;
        }
    },
    actions: {
        setOpenSideBar(context, isMobile) {
            context.commit('setOpenSideBarState', isMobile);
        },
        setScreenId(context, screenid) {
            try {
                if (screenid) {
                    context.commit('setScreenId', screenid);
                    // repository.checkScreenAuthority(screenid).then((res) => {
                    //     const { data } = res;
                    //     if (data.Code === 200) {
                    //         if (data.Data.screenauthority == 0) {
                    //             Router.push({ name: 'Bs002000i' });
                    //         } else {
                    //             context.commit(
                    //                 'setScreenAuthority',
                    //                 data.Data.screenauthority
                    //             );
                    //         }
                    //     }
                    // });
                }
            } catch (e) {
                console.log('Action setScreenId: ' + e.message);
            }
        },
        getNotify(context, { code }) {
            try {
                repository.getNotify(code).then((res) => {
                    const { data } = res;
                    if (data.Code === 200) {
                        context.commit('setNotification', data.Data ?? {});
                    }
                });
            } catch (e) {
                console.log('Action setNotification: ' + e.message);
            }
        },
        saveNotify(context, { code }) {
            try {
                repository.saveNotify(code).then((res) => {
                    const { data } = res;
                    if (data.Code === 200) {
                        if (data.Data[0].statusCode === '0') {
                            context.commit('setNotification', {});
                        }
                    }
                });
            } catch (e) {
                console.log('Action saveNotification: ' + e.message);
            }
        },
        updatePassWord(context, input) {
            try {
                repository.changePassWord(input.data).then((res) => {
                    const { data } = res;
                    if (data.Code === 200) {
                        if (data.Data[0].statusCode === '0') {
                            // context.commit('setNotification', {});
                            this.commit('app/showModalMessage', {
                                title: MSG_TITLE.S059,
                                content: messages.E163,
                                okText: 'OK'
                            });
                            input.callback(0);
                        }
                    } else if (data.Code == 423) {
                        input.callback(1);
                    }
                });
            } catch (e) {
                console.log('Action savePassword: ' + e.message);
            }
        },
        /**
         * postCommonHeader
         * -----------------------------------------
         * @author : hainn - 2024/11/26 - create
         * @params :
         * @access : private
         */
        postCommonHeader(context, input) {
            try {
                repository.postCommonHeader(input).then((res) => {
                    const { data } = res;
                    if (data.Code === 200) {
                        context.commit('setOrderNumber', data.Data.order_number);
                    } else if (data.Code === 422) {
                        if (input?.onErrorValidate) {
                            input?.onErrorValidate(data.Message[0]);
                        }
                    } else {
                        if (input?.onError) {
                            input?.onError(data.Data.message);
                        }
                    }
                });
            } catch (e) {
                console.log('Action get data screen: ' + e.message);
            }
        },
        /**
         * getNotificationMessage
         * -----------------------------------------
         * @author : phuongdt - 2025/04/04 - create
         * @params :
         * @access : private
         */
        getNotificationMessage(context) {
            try {
                repository
                    .getNotificationMessage({
                        version: helpers.version()
                    })
                    .then((res) => {
                        const { data } = res;
                        if (data.Code === 200) {
                            const listMessage = res.data.Data.notification;
                            context.commit('setDM', res.data.Data.msgCount?.UnreadCount ?? 0);
                            listMessage.map((message) => {
                                helpers.notificationApi(message);
                            });
                        }
                    });
            } catch (e) {
                console.log('Action get data screen: ' + e.message);
            }
        },
        getDmTopCount(context) {
            try {
                repository.getDmTopCount().then((res) => {
                    const { data } = res;
                    if (data.Code === 200) {
                        context.commit('setDM', res.data.Data.msgCount?.UnreadCount ?? 0);
                    }
                });
            } catch (e) {
                console.log('Action get data screen: ' + e.message);
            }
        }
    }
};
